import type { StaticImageData } from 'next/image';
import type { GmRewardAction, GmStatusKey } from '@/components/features/gamification/model/types/gm-statuses';
import type { GroupedGamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { statusKeys } from '@/components/features/gamification/model/constants/statuses';
import BIRTHDAY_BONUS from '@/public/assets/image/gamification/perks/birthday-bonus@3x.webp';
import EXCLUSIVE_OFFERS from '@/public/assets/image/gamification/perks/exclusive-offers@3x.webp';
import FREE_SPINS from '@/public/assets/image/gamification/perks/free-spins@3x.webp';
import HIGHER_DAILY_REDEMPTION from '@/public/assets/image/gamification/perks/higher-daily-redemption@3x.webp';
import LEVEL_UP_SCRATCH_CARD from '@/public/assets/image/gamification/perks/level-up-scratch-card@3x.webp';
import LOSS_INSURANCE from '@/public/assets/image/gamification/perks/loss-insurance@3x.webp';
import MONTHLY_PLAYBACK from '@/public/assets/image/gamification/perks/monthly-playback@3x.webp';
import PERSONAL_CONCIERGE from '@/public/assets/image/gamification/perks/personal-concierge@3x.webp';
import PERSONAL_SLOT_GAME from '@/public/assets/image/gamification/perks/personal-slot-game@3x.webp';

import PRIORITY_REDEMPTIONS from '@/public/assets/image/gamification/perks/priority-redemptions@3x.webp';
import PURCHASE_BONUS from '@/public/assets/image/gamification/perks/purchase-bonus@3x.webp';
import REFERRAL from '@/public/assets/image/gamification/perks/referral@3x.webp';
import STATUS_UP_SCRATCH_CARD from '@/public/assets/image/gamification/perks/status-up-scratch-card@3x.webp';
import WEEKLY_PLAYBACK from '@/public/assets/image/gamification/perks/weekly-playback@3x.webp';
import WINNERS_POOL from '@/public/assets/image/gamification/perks/winners-pool@3x.webp';
import BIRTHDAY_BONUS_REWARD from '@/public/assets/image/gamification/rewards/birthday-bonus@2x.webp';
import EXCLUSIVE_OFFERS_REWARD from '@/public/assets/image/gamification/rewards/exclusive-offers@2x.webp';
import FREE_SPINS_REWARD from '@/public/assets/image/gamification/rewards/free-spins@2x.webp';
import HIGHER_DAILY_REDEMPTION_REWARD from '@/public/assets/image/gamification/rewards/higher-daily-redemption@2x.webp';
import LEVEL_UP_SCRATCH_CARD_REWARD from '@/public/assets/image/gamification/rewards/level-up-scratch-card@2x.webp';
import LOSS_INSURANCE_REWARD from '@/public/assets/image/gamification/rewards/loss-insurance@2x.webp';
import MONTHLY_PLAYBACK_REWARD from '@/public/assets/image/gamification/rewards/monthly-playback@2x.webp';
import PERSONAL_CONCIERGE_REWARD from '@/public/assets/image/gamification/rewards/personal-concierge@2x.webp';
import PERSONAL_SLOT_GAME_REWARD from '@/public/assets/image/gamification/rewards/personal-slot-game@2x.webp';
import PRIORITY_REDEMPTIONS_REWARD from '@/public/assets/image/gamification/rewards/priority-redemptions@2x.webp';
import PURCHASE_BONUS_REWARD from '@/public/assets/image/gamification/rewards/purchase-bonus@2x.webp';
import REFERRAL_REWARD from '@/public/assets/image/gamification/rewards/referral@2x.webp';
import STATUS_UP_SCRATCH_CARD_REWARD from '@/public/assets/image/gamification/rewards/status-up-scratch-card@2x.webp';
import WEEKLY_PLAYBACK_REWARD from '@/public/assets/image/gamification/rewards/weekly-playback@2x.webp';
import WINNERS_POOL_REWARD from '@/public/assets/image/gamification/rewards/winners-pool@2x.webp';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import messages from '@/translation/en.json';

const { titles } = messages.gamification.perks;
export type GmRewardButtonLabelKey = {
  [Key in keyof typeof messages.gamification]: typeof messages.gamification[Key] extends string ? Key : never;
}[keyof typeof messages.gamification];

const getAllStatusValues = (value: string): Record<GmStatusKey, string> => {
  return Object.fromEntries(statusKeys.map(statusKey => [statusKey, value])) as Record<GmStatusKey, string>;
};

export type RewardNotificationType = 'gift' | 'count';

export interface StatusRewardBenefit {
  translationKey: string;
  perkImg: StaticImageData;
  rewardImg: StaticImageData;
  type: GroupedGamificationRewardType;
  title: string;
  rewardNotificationBadge?: RewardNotificationType;
  values: Partial<Record<GmStatusKey, string>>;
  action?: GmRewardAction;
  link?: string;
  buttonLabelKey?: GmRewardButtonLabelKey;
  /** Keeps the benefit locked until the frontend launches it. */
  comingSoon?: boolean;
  /**
   * Benefit unlocks from the lowest status listed in `values` when the API
   * provides no numeric level requirement.
   */
  unlocksWithStatus?: boolean;
}

const statusRewardBenefits = [
  {
    translationKey: 'loss-insurance',
    comingSoon: true,
    type: GamificationRewardType.LossInsurance,
    perkImg: LOSS_INSURANCE,
    rewardImg: LOSS_INSURANCE_REWARD,
    title: titles['loss-insurance'],
    values: {
      starter: 'up to 2.5%',
      bronze: 'up to 5%',
      silver: 'up to 10%',
      gold: 'up to 25%',
      platinum: 'up to 100%',
      diamond: 'up to 250%',
      master: 'up to 250%',
      grandmaster: 'up to 250%',
      champion: 'up to 250%',
      legend: 'up to 500%',
    },
  },
  {
    translationKey: 'winners-pool',
    comingSoon: true,
    type: GamificationRewardType.WinnersPool,
    perkImg: WINNERS_POOL,
    rewardImg: WINNERS_POOL_REWARD,
    title: titles['winners-pool'],
    values: getAllStatusValues('up to 150k SC'),
  },
  {
    translationKey: 'level-up-scratch-card',
    perkImg: LEVEL_UP_SCRATCH_CARD,
    rewardImg: LEVEL_UP_SCRATCH_CARD_REWARD,
    type: GamificationRewardType.LevelUp,
    title: titles['level-up-scratch-card'],
    link: '/gamification/level-up',
    action: 'level-up-card',
    values: {
      starter: 'up to 0.5 SC',
      bronze: 'up to 1 SC',
      silver: 'up to 2.5 SC',
      gold: 'up to 5 SC',
      platinum: 'up to 7.5 SC',
      diamond: 'up to 10 SC',
      master: 'up to 15 SC',
      grandmaster: 'up to 20 SC',
      champion: 'up to 50 SC',
      legend: 'up to 1000 SC',
    },
  },
  {
    translationKey: 'higher-daily-redemption',
    unlocksWithStatus: true,
    perkImg: HIGHER_DAILY_REDEMPTION,
    rewardImg: HIGHER_DAILY_REDEMPTION_REWARD,
    title: titles['higher-daily-redemption'],
    type: GamificationRewardType.HigherDailyRedemption,
    action: 'gm-reward-redeem-instruction',
    values: {
      starter: 'up to 100 SC / 24 h',
      bronze: 'up to 200 SC / 24 h',
      silver: 'up to 500 SC / 24 h',
      gold: 'up to 1000 SC / 24 h',
      platinum: 'up to 1500 SC / 24 h',
      diamond: 'up to 2500 SC / 24 h',
      master: 'up to 5000 SC / 24 h',
      grandmaster: 'up to 10000 SC / 24 h',
      champion: 'up to 20000 SC / 24 h',
      legend: 'Custom / 24h',
    },
  },
  {
    translationKey: 'status-up-scratch-card',
    perkImg: STATUS_UP_SCRATCH_CARD,
    rewardImg: STATUS_UP_SCRATCH_CARD_REWARD,
    type: GamificationRewardType.StatusUp,
    title: titles['status-up-scratch-card'],
    action: 'status-up-card',
    link: '/gamification/status-up',
    values: {
      bronze: 'up to 2 SC',
      silver: 'up to 5 SC',
      gold: 'up to 10 SC',
      platinum: 'up to 20 SC',
      diamond: 'up to 30 SC',
      master: 'up to 50 SC',
      grandmaster: 'up to 75 SC',
      champion: 'up to 100 SC',
      legend: 'up to 150 SC',
    },
  },
  {
    translationKey: 'referral',
    comingSoon: false,
    type: GamificationRewardType.Referral,
    perkImg: REFERRAL,
    rewardImg: REFERRAL_REWARD,
    action: 'open-referral-page',
    title: titles.referral,
    rewardNotificationBadge: 'gift',
    values: {
      bronze: 'up to 3500 SC',
      silver: 'up to 3500 SC',
      gold: 'up to 3500 SC',
      platinum: 'up to 3500 SC',
      diamond: 'up to 3500 SC',
      master: 'up to 3500 SC',
      grandmaster: 'up to 3500 SC',
      champion: 'up to 3500 SC',
      legend: 'up to 3500 SC',
    },
  },
  {
    translationKey: 'free-spins',
    comingSoon: true,
    perkImg: FREE_SPINS,
    rewardImg: FREE_SPINS_REWARD,
    type: GamificationRewardType.FreeSpins,
    title: titles['free-spins'],
    values: {
      silver: '10 spins × 0.20 SC',
      gold: '20 spins × 0.20 SC',
      platinum: '30 spins × 0.20 SC',
      diamond: '50 spins × 0.20 SC',
      master: '70 spins × 0.20 SC',
      grandmaster: '100 spins × 0.20 SC',
      champion: '150 spins × 0.20 SC',
      legend: '200 spins × 0.20 SC',
    },
  },
  {
    translationKey: 'weekly-playback',
    comingSoon: true,
    perkImg: WEEKLY_PLAYBACK,
    rewardImg: WEEKLY_PLAYBACK_REWARD,
    buttonLabelKey: 'claim-now',
    type: GamificationRewardType.WeeklyPlayback,
    title: titles['weekly-playback'],
    values: {
      silver: '1%',
      gold: '1%',
      platinum: '1%',
      diamond: '1.2%',
      master: '1.5%',
      grandmaster: '1.5%',
      champion: '2%',
      legend: '2.5%',
    },
  },
  {
    translationKey: 'priority-redemptions',
    unlocksWithStatus: true,
    perkImg: PRIORITY_REDEMPTIONS,
    rewardImg: PRIORITY_REDEMPTIONS_REWARD,
    type: GamificationRewardType.PriorityRedemptions,
    title: titles['priority-redemptions'],
    values: {
      gold: 'Processed first',
      platinum: 'Processed first',
      diamond: 'Processed first',
      master: 'Processed first',
      grandmaster: 'Processed first',
      champion: 'Processed first',
      legend: 'Processed first',
    },
  },
  {
    translationKey: 'monthly-playback',
    comingSoon: true,
    perkImg: MONTHLY_PLAYBACK,
    rewardImg: MONTHLY_PLAYBACK_REWARD,
    buttonLabelKey: 'claim-now',
    type: GamificationRewardType.MonthlyPlayback,
    title: titles['monthly-playback'],
    values: {
      gold: '1%',
      platinum: '1.5%',
      diamond: '2%',
      master: '2.5%',
      grandmaster: '3%',
      champion: '4%',
      legend: '5%',
    },
  },
  {
    translationKey: 'birthday-present',
    comingSoon: true,
    perkImg: BIRTHDAY_BONUS,
    rewardImg: BIRTHDAY_BONUS_REWARD,
    buttonLabelKey: 'claim-now',
    type: GamificationRewardType.BirthdayPresent,
    title: titles['birthday-present'],
    values: {
      gold: 'up to 100 SC',
      platinum: 'up to 250 SC',
      diamond: 'up to 500 SC',
      master: 'up to 1000 SC',
      grandmaster: 'up to 2500 SC',
      champion: 'up to 5000 SC',
      legend: 'up to 10000 SC',
    },
  },
  {
    translationKey: 'personal-concierge',
    unlocksWithStatus: true,
    action: 'open-support',
    perkImg: PERSONAL_CONCIERGE,
    rewardImg: PERSONAL_CONCIERGE_REWARD,
    type: GamificationRewardType.PersonalConcierge,
    title: titles['personal-concierge'],
    values: {
      platinum: 'Tailored Service',
      diamond: 'Tailored Service',
      master: 'Tailored Service',
      grandmaster: 'Tailored Service',
      champion: 'Tailored Service',
      legend: 'Tailored Service',
    },
  },
  {
    translationKey: 'exclusive-offers',
    unlocksWithStatus: true,
    action: 'open-support',
    perkImg: EXCLUSIVE_OFFERS,
    rewardImg: EXCLUSIVE_OFFERS_REWARD,
    type: GamificationRewardType.ExclusiveOffers,
    title: titles['exclusive-offers'],
    values: {
      platinum: 'Made for you',
      diamond: 'Made for you',
      master: 'Made for you',
      grandmaster: 'Made for you',
      champion: 'Made for you',
      legend: 'Made for you',
    },
  },
  {
    translationKey: 'purchase-bonus',
    perkImg: PURCHASE_BONUS,
    rewardImg: PURCHASE_BONUS_REWARD,
    type: GamificationRewardType.PurchaseBonus,
    title: titles['purchase-bonus'],
    unlocksWithStatus: true,
    values: {
      platinum: '50%',
      diamond: '100%',
      master: '150%',
      grandmaster: '250%',
      champion: '500%',
      legend: '1,000%',
    },
  },
  {
    translationKey: 'personal-slot-game',
    perkImg: PERSONAL_SLOT_GAME,
    rewardImg: PERSONAL_SLOT_GAME_REWARD,
    type: GamificationRewardType.PersonalSlotGame,
    title: titles['personal-slot-game'],
    unlocksWithStatus: true,
    values: {
      grandmaster: 'Crafted for you',
      champion: 'Crafted for you',
      legend: 'Crafted for you',
    },
  },
] as const satisfies readonly StatusRewardBenefit[];

export type StatusRewardView = 'statuses' | 'rewards';

/**
 * Display order per page. The statuses page keeps the declaration order above;
 * list types here only for pages that need a different order. Unlisted types
 * are appended in declaration order.
 */
const VIEW_ORDER: Partial<Record<StatusRewardView, readonly GroupedGamificationRewardType[]>> = {
  rewards: [
    GamificationRewardType.LossInsurance,
    GamificationRewardType.Referral,
    GamificationRewardType.WinnersPool,
    GamificationRewardType.LevelUp,
    GamificationRewardType.HigherDailyRedemption,
    GamificationRewardType.StatusUp,
  ],
};

export const STATUS_REWARD_BENEFITS: readonly (StatusRewardBenefit)[] = statusRewardBenefits;

export const getStatusRewardBenefits = (view: StatusRewardView): readonly StatusRewardBenefit[] => {
  const order = VIEW_ORDER[view];
  if (!order) {
    return STATUS_REWARD_BENEFITS;
  }

  const rank = (type: GroupedGamificationRewardType) => {
    const index = order.indexOf(type);
    return index === -1 ? order.length : index;
  };

  // Array#sort is stable, so unlisted types keep their declaration order.
  return [...STATUS_REWARD_BENEFITS].sort((a, b) => rank(a.type) - rank(b.type));
};
